import classNames from 'classnames'
import { useCallback, useMemo } from 'react'
import { Responsive, WidthProvider } from 'react-grid-layout'

import type { ViewProps } from '../common-props'
import type { ReactElement } from 'react'
import type { IBuilderConfig } from '~/types/graph-builder'

import { viewClassName } from '../common-props'

import ContentLayout from '~/components/ContentLayout'
import DynamicGraphBlock from '~/components/DynamicGraphBlock'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import { COMMON_GRID_PROPS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAnalyticsContext } from '~/providers/Analytics'
import { useAppSummary } from '~/providers/AppSummary'
import { useChartConfigsListQuery } from '~/queries/graphs-builder'
import { CA_PAGES, CA_SECTIONS } from '~/types/graph-builder'
import { formatConfig } from '~/utils/graphs-builder'
import { getGraphLayoutPosition } from '~/utils/grid'
import { capitalize } from '~/utils/string'

const ResponsiveGridLayout = WidthProvider(Responsive)

const PAGE = CA_PAGES.APP_USAGE

const AppUsage = ({ analyticsApiFn, eventsData, isAppLoading, kpiField }: ViewProps): ReactElement => {
  /** Local state */

  const { appKey, deviceType } = useAppSummary()

  const { state } = useAnalyticsContext()

  const layoutClassNames = classNames(viewClassName, { 'filters-collapsed': state.isFiltersCollapsed })

  /** Queries & Mutations */

  const { data: chartConfigs, isLoading } = useChartConfigsListQuery({
    appKey,
    deviceType,
    pageName: PAGE,
  })

  const formattedConfigs = useMemo(
    () => formatConfig(chartConfigs?.data) as unknown as IBuilderConfig[],
    [chartConfigs?.data]
  )

  /** Effects */

  useDocumentTitle('CA App Usage')

  /** Handlers */

  const graphOptions = useCallback(
    (config: IBuilderConfig) => ({
      apiSelectionFn: analyticsApiFn,
      appKey,
      canBeFavourite: false,
      config,
      deviceType,
      eventsData,
      isAppLoading,
      kpiField,
    }),
    [analyticsApiFn, appKey, deviceType, eventsData, isAppLoading, kpiField]
  )

  /** Render */

  return (
    <Layout className={layoutClassNames} withMargin>
      <ContentLayout loading={isLoading || isAppLoading}>
        <Section title="">
          <ResponsiveGridLayout {...COMMON_GRID_PROPS} compactType={null}>
            {formattedConfigs
              .filter(({ section }) => !section)
              .map((config, idx) => (
                <div data-grid={getGraphLayoutPosition(`section-1-${idx}`, idx)} key={idx}>
                  <DynamicGraphBlock {...graphOptions(config)} />
                </div>
              ))}
          </ResponsiveGridLayout>
        </Section>
        {Object.values(CA_SECTIONS[PAGE]).map((section) => (
          <Section key={section} title={capitalize(section)}>
            <ResponsiveGridLayout {...COMMON_GRID_PROPS} compactType={null}>
              {formattedConfigs
                .filter(({ section: s }) => s === section)
                .map((config, idx) => (
                  <div data-grid={getGraphLayoutPosition(`section-2-${idx}`, idx)} key={idx}>
                    <DynamicGraphBlock {...graphOptions(config)} />
                  </div>
                ))}
            </ResponsiveGridLayout>
          </Section>
        ))}
      </ContentLayout>
    </Layout>
  )
}

export default AppUsage
